import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Plus, Store, Filter, X, Check, Clock,
  DollarSign, Pencil, Trash2, Link2, Copy,
  Wallet, CreditCard, Calendar, FileText, Settings, Euro, RefreshCw, Search, UserPlus,
  MessageCircle
} from "lucide-react";

const revendaSchema = z.object({
  nome: z.string().min(2, "Mínimo 2 caracteres"),
  pix_1: z.string().optional().default(""),
  pix_1_banco: z.string().optional().default(""),
  pix_1_nome: z.string().optional().default(""),
  pix_2: z.string().optional().default(""),
  pix_2_banco: z.string().optional().default(""),
  pix_2_nome: z.string().optional().default(""),
});

const servicoSchema = z.object({
  nome: z.string().min(1, "Nome obrigatório"),
  valor: z.coerce.number().min(0, "Valor inválido"),
  moeda: z.string().min(1, "Moeda obrigatória"),
});

const lancamentoSchema = z.object({
  tipo_credito: z.string().default("MEC"),
  mec_creditos: z.string().optional().default(""),
  data: z.string().min(1, "Data obrigatória"),
  servico: z.string().optional().default(""),
  valor: z.coerce.number().min(0, "Valor inválido"),
  moeda: z.string().default("R$"),
  descricao: z.string().optional().default(""),
});

const formatCurrency = (value, moeda = "R$") => {
  if (moeda === "€") {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR"
    }).format(value);
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

// Revenda Card Component
const RevendaCard = ({ revenda, servicos, onEdit, onDelete, onAddLancamento, onEditLancamento, onDeleteLancamento, onDeleteAllLancamentos, onToggleConfirmado, onCopyLink, onWhatsApp, onManageServicos, refreshKey }) => {
  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const { authAxios } = useAuth();

  const fetchLancamentos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/revendas/${revenda.id}/lancamentos`);
      setLancamentos(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [authAxios, revenda.id]);

  useEffect(() => {
    fetchLancamentos();
  }, [revenda.id, refreshKey, fetchLancamentos]);

  const handleToggle = async (lancamento) => {
    await onToggleConfirmado(lancamento);
    fetchLancamentos();
  };

  const handleDeleteAll = async () => {
    await onDeleteAllLancamentos(revenda);
    fetchLancamentos();
  };

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Store className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">{revenda.nome}</CardTitle>
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
              onClick={() => onCopyLink(revenda)}
              title="Copiar link de visualização"
              data-testid={`copy-link-btn-${revenda.id}`}
            >
              <Link2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
              onClick={() => onWhatsApp(revenda)}
              title="Enviar link pelo WhatsApp"
              data-testid={`whatsapp-btn-${revenda.id}`}
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onManageServicos(revenda)}
              title="Gerenciar Serviços"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(revenda)}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onDelete(revenda.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/30 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <DollarSign className="w-3 h-3 text-blue-500" />
              <span className="text-[10px] font-bold text-blue-500">SOMA TOTAL</span>
            </div>
            <p className="text-lg font-bold text-blue-500 number-display">
              {formatCurrency(revenda.soma_total || 0)}
            </p>
          </div>
          
          <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Wallet className="w-3 h-3 text-emerald-500" />
              <span className="text-[10px] font-bold text-emerald-500">SALDO</span>
            </div>
            <p className="text-lg font-bold text-emerald-500 number-display">
              {formatCurrency(revenda.saldo)}
            </p>
          </div>
          
          <div className="p-2 bg-amber-500/10 rounded-lg border border-amber-500/30 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CreditCard className="w-3 h-3 text-amber-500" />
              <span className="text-[10px] font-bold text-amber-500">A PAGAR</span>
            </div>
            <p className="text-lg font-bold text-amber-500 number-display">
              {formatCurrency(revenda.valor_a_pagar)}
            </p>
          </div>
        </div>

        {/* Add Lancamento Button */}
        <Button 
          variant="outline" 
          className="w-full mb-4"
          onClick={() => onAddLancamento(revenda)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Lançamento
        </Button>

        {/* Serviços cadastrados */}
        {servicos.length > 0 && (
          <div className="mb-4 p-3 bg-muted/30 rounded-lg">
            <p className="text-xs font-medium text-muted-foreground mb-2">Serviços cadastrados:</p>
            <div className="flex flex-wrap gap-2">
              {servicos.map((s) => (
                <Badge key={s.id} variant="outline" className="text-xs">
                  {s.nome}: {formatCurrency(s.valor, s.moeda)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Lancamentos List */}
        <div className="mt-2">
          <p className="text-sm font-medium mb-3">Lancamentos ({lancamentos.length})</p>
          {loading ? (
            <div className="text-center py-4">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : lancamentos.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum lancamento
            </p>
          ) : (
            <div className="space-y-2">
              {lancamentos.map((l) => (
                <div 
                  key={l.id} 
                  className={`p-3 rounded-lg border ${l.confirmado ? "bg-emerald-500/5 border-emerald-500/30" : "bg-amber-500/5 border-amber-500/30"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{l.servico}</span>
                        <Badge variant={l.confirmado ? "default" : "secondary"} className={l.confirmado ? "bg-emerald-500" : "bg-amber-500"}>
                          {l.confirmado ? "Saldo" : "A Pagar"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {l.data}
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {l.mec_creditos} {l.tipo_credito === "Creditos" ? "Creditos" : "MEC"}
                        </div>
                        <div className="flex items-center gap-1 font-semibold text-foreground">
                          {l.moeda === "€" ? <Euro className="w-3 h-3" /> : <DollarSign className="w-3 h-3" />}
                          {formatCurrency(l.valor, l.moeda)}
                        </div>
                      </div>
                      {l.descricao && (
                        <p className="mt-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                          {l.descricao}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <Button
                        variant={l.confirmado ? "default" : "outline"}
                        size="sm"
                        className={`h-7 text-xs ${l.confirmado ? "bg-emerald-500 hover:bg-emerald-600" : ""}`}
                        onClick={() => handleToggle(l)}
                      >
                        {l.confirmado ? (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            Confirmado
                          </>
                        ) : (
                          "Confirmar"
                        )}
                      </Button>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onEditLancamento(l, revenda)}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => onDeleteLancamento(l.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Botao Excluir Todos */}
              <div className="pt-3 border-t border-border mt-3">
                <Button variant="destructive" size="sm" className="w-full" onClick={handleDeleteAll} data-testid="delete-all-lancamentos">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Todos os Lancamentos
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function RevendaPage() {
  const { authAxios } = useAuth();
  const [revendas, setRevendas] = useState([]);
  const [servicosPorRevenda, setServicosPorRevenda] = useState({});
  const [stats, setStats] = useState({ total_revendas: 0, total_soma: 0, total_saldo: 0, total_a_pagar: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Dialogs
  const [revendaDialogOpen, setRevendaDialogOpen] = useState(false);
  const [lancamentoDialogOpen, setLancamentoDialogOpen] = useState(false);
  const [servicoDialogOpen, setServicoDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [revendaToDeleteAll, setRevendaToDeleteAll] = useState(null);
  const [editingRevenda, setEditingRevenda] = useState(null);
  const [editingLancamento, setEditingLancamento] = useState(null);
  const [editingServico, setEditingServico] = useState(null);
  const [selectedRevenda, setSelectedRevenda] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [filterNome, setFilterNome] = useState("");

  // Funcionarios
  const [funcionarios, setFuncionarios] = useState([]);
  const [dConfigFunc, setDConfigFunc] = useState(false);
  const [novoFunc, setNovoFunc] = useState("");

  const revendaForm = useForm({
    resolver: zodResolver(revendaSchema),
    defaultValues: { nome: "", email: "", senha: "" },
  });

  const servicoForm = useForm({
    resolver: zodResolver(servicoSchema),
    defaultValues: { nome: "", valor: 0, moeda: "R$" },
  });

  const lancamentoForm = useForm({
    resolver: zodResolver(lancamentoSchema),
    defaultValues: { tipo_credito: "MEC", mec_creditos: "", data: new Date().toISOString().split("T")[0], servico: "", valor: 0, moeda: "R$", descricao: "" },
  });

  const fetchRevendas = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterNome) params.append("nome", filterNome);
      
      const res = await authAxios.get(`/revendas?${params.toString()}`);
      setRevendas(res.data);
      
      // Fetch servicos in parallel
      const servicosResults = await Promise.all(
        res.data.map(revenda =>
          authAxios.get(`/revendas/${revenda.id}/servicos`).then(r => ({ id: revenda.id, data: r.data })).catch(() => ({ id: revenda.id, data: [] }))
        )
      );
      const servicosMap = {};
      servicosResults.forEach(r => { servicosMap[r.id] = r.data; });
      setServicosPorRevenda(servicosMap);
    } catch (err) {
      toast.error("Erro ao carregar revendas");
    } finally {
      setLoading(false);
    }
  }, [authAxios, filterNome]);

  const fetchStats = async () => {
    try {
      const res = await authAxios.get("/revendas/stats/summary");
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFuncionarios = async () => {
    try { const res = await authAxios.get("/hub-empresa/funcionarios"); setFuncionarios(res.data); }
    catch { }
  };

  useEffect(() => {
    fetchRevendas();
    fetchStats();
    fetchFuncionarios();
  }, [fetchRevendas]);

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchRevendas();
      await fetchStats();
      setRefreshKey(prev => prev + 1);
      toast.success("Dados atualizados!");
    } catch (err) {
      toast.error("Erro ao atualizar");
    } finally {
      setRefreshing(false);
    }
  };

  // Revenda handlers
  const handleOpenRevendaDialog = (revenda = null) => {
    if (revenda) {
      setEditingRevenda(revenda);
      revendaForm.reset({ 
        nome: revenda.nome,
        pix_1: revenda.pix_1 || "",
        pix_1_banco: revenda.pix_1_banco || "",
        pix_1_nome: revenda.pix_1_nome || "",
        pix_2: revenda.pix_2 || "",
        pix_2_banco: revenda.pix_2_banco || "",
        pix_2_nome: revenda.pix_2_nome || ""
      });
    } else {
      setEditingRevenda(null);
      revendaForm.reset({ nome: "", pix_1: "", pix_1_banco: "", pix_1_nome: "", pix_2: "", pix_2_banco: "", pix_2_nome: "" });
    }
    setRevendaDialogOpen(true);
  };

  const handleSubmitRevenda = async (data) => {
    setSubmitting(true);
    try {
      if (editingRevenda) {
        await authAxios.put(`/revendas/${editingRevenda.id}`, { 
          nome: data.nome,
          pix_1: data.pix_1 || "",
          pix_1_banco: data.pix_1_banco || "",
          pix_1_nome: data.pix_1_nome || "",
          pix_2: data.pix_2 || "",
          pix_2_banco: data.pix_2_banco || "",
          pix_2_nome: data.pix_2_nome || ""
        });
        toast.success("Revenda atualizada!");
      } else {
        await authAxios.post("/revendas", { 
          nome: data.nome,
          pix_1: data.pix_1 || "",
          pix_1_banco: data.pix_1_banco || "",
          pix_1_nome: data.pix_1_nome || "",
          pix_2: data.pix_2 || "",
          pix_2_banco: data.pix_2_banco || "",
          pix_2_nome: data.pix_2_nome || ""
        });
        toast.success("Revenda cadastrada!");
      }
      setRevendaDialogOpen(false);
      fetchRevendas();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao salvar revenda");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRevenda = async (id) => {
    if (!window.confirm("Excluir esta revenda e todos os lançamentos?")) return;
    try {
      await authAxios.delete(`/revendas/${id}`);
      toast.success("Revenda excluída!");
      fetchRevendas();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao excluir revenda");
    }
  };

  // Lancamento handlers
  const handleOpenLancamentoDialog = (revenda, lancamento = null) => {
    setSelectedRevenda(revenda);
    if (lancamento) {
      setEditingLancamento(lancamento);
      lancamentoForm.reset({
        tipo_credito: lancamento.tipo_credito || "MEC",
        mec_creditos: lancamento.mec_creditos,
        data: lancamento.data,
        servico: lancamento.servico,
        valor: lancamento.valor,
        moeda: lancamento.moeda || "R$",
        descricao: lancamento.descricao || "",
      });
    } else {
      setEditingLancamento(null);
      lancamentoForm.reset({
        tipo_credito: "MEC",
        mec_creditos: "",
        data: new Date().toISOString().split("T")[0],
        servico: "",
        valor: 0,
        moeda: "R$",
        descricao: "",
      });
    }
    setLancamentoDialogOpen(true);
  };

  const handleSubmitLancamento = async (data) => {
    setSubmitting(true);
    try {
      if (editingLancamento) {
        await authAxios.put(`/revendas/lancamentos/${editingLancamento.id}`, data);
        toast.success("Lançamento atualizado!");
      } else {
        await authAxios.post("/revendas/lancamentos", {
          ...data,
          revenda_id: selectedRevenda.id,
          confirmado: false,
        });
        toast.success("Lançamento cadastrado!");
      }
      setLancamentoDialogOpen(false);
      setRefreshKey(prev => prev + 1);
      fetchRevendas();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao salvar lançamento");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLancamento = async (id) => {
    if (!window.confirm("Excluir este lançamento?")) return;
    try {
      await authAxios.delete(`/revendas/lancamentos/${id}`);
      toast.success("Lançamento excluído!");
      setRefreshKey(prev => prev + 1);
      fetchRevendas();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao excluir lançamento");
    }
  };

  // Delete All Lancamentos handlers
  const handleOpenDeleteAllDialog = (revenda) => {
    setRevendaToDeleteAll(revenda);
    setDeleteAllDialogOpen(true);
  };

  const handleConfirmDeleteAll = async () => {
    if (!revendaToDeleteAll) return;
    
    try {
      const res = await authAxios.delete(`/revendas/${revendaToDeleteAll.id}/lancamentos/all`);
      toast.success(res.data.message);
      setDeleteAllDialogOpen(false);
      setRevendaToDeleteAll(null);
      await fetchRevendas();
      await fetchStats();
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      toast.error("Erro ao excluir lançamentos");
    }
  };

  const handleToggleConfirmado = async (lancamento) => {
    try {
      await authAxios.put(`/revendas/lancamentos/${lancamento.id}`, {
        confirmado: !lancamento.confirmado,
      });
      toast.success(lancamento.confirmado ? "Desmarcado como confirmado" : "Confirmado! Valor somado ao saldo.");
      await fetchRevendas();
      await fetchStats();
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      toast.error("Erro ao atualizar lançamento");
    }
  };

  const handleCopyLink = async (revenda) => {
    try {
      let tokenPublico = revenda.token_publico;
      
      // Se não tem token, gera um
      if (!tokenPublico) {
        const res = await authAxios.post(`/revendas/${revenda.id}/gerar-token`);
        tokenPublico = res.data.token_publico;
        // Atualiza a lista local
        fetchRevendas();
      }
      
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/revenda-publico/${tokenPublico}`;
      
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado!", {
        description: `Link de ${revenda.nome} copiado para a área de transferência`
      });
    } catch (err) {
      console.error("Erro ao copiar link:", err);
      toast.error("Erro ao copiar link");
    }
  };

  const handleWhatsAppRevenda = async (revenda) => {
    try {
      let tokenPublico = revenda.token_publico;
      if (!tokenPublico) {
        const res = await authAxios.post(`/revendas/${revenda.id}/gerar-token`);
        tokenPublico = res.data.token_publico;
        fetchRevendas();
      }
      const link = `${window.location.origin}/revenda-publico/${tokenPublico}`;
      const msg = `💼 *Área da Revenda – Verificar Débito*\n🚀 *_Confira todas as informações no link abaixo_*\n🔗 ${link}`;
      const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank");
    } catch (err) {
      console.error("Erro ao gerar link WhatsApp:", err);
      toast.error("Erro ao abrir WhatsApp");
    }
  };

  // Servico handlers
  const handleOpenServicoDialog = (revenda, servico = null) => {
    setSelectedRevenda(revenda);
    if (servico) {
      setEditingServico(servico);
      servicoForm.reset({ nome: servico.nome, valor: servico.valor, moeda: servico.moeda });
    } else {
      setEditingServico(null);
      servicoForm.reset({ nome: "", valor: 0, moeda: "R$" });
    }
    setServicoDialogOpen(true);
  };

  const handleSubmitServico = async (data) => {
    setSubmitting(true);
    try {
      if (editingServico) {
        await authAxios.put(`/revendas/servicos/${editingServico.id}`, data);
        toast.success("Serviço atualizado!");
      } else {
        await authAxios.post("/revendas/servicos", {
          ...data,
          revenda_id: selectedRevenda.id,
        });
        toast.success("Serviço cadastrado!");
      }
      setServicoDialogOpen(false);
      fetchRevendas();
    } catch (err) {
      toast.error("Erro ao salvar serviço");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteServico = async (id) => {
    if (!window.confirm("Excluir este serviço?")) return;
    try {
      await authAxios.delete(`/revendas/servicos/${id}`);
      toast.success("Serviço excluído!");
      fetchRevendas();
    } catch (err) {
      toast.error("Erro ao excluir serviço");
    }
  };

  // Função para selecionar um serviço e preencher valor/moeda
  const handleSelectServico = (servicoId) => {
    const servicos = servicosPorRevenda[selectedRevenda?.id] || [];
    const servico = servicos.find(s => s.id === servicoId);
    if (servico) {
      lancamentoForm.setValue("servico", servico.nome);
      lancamentoForm.setValue("moeda", servico.moeda);
      const tipo = lancamentoForm.getValues("tipo_credito");
      if (tipo === "Creditos") {
        const creditos = parseFloat(lancamentoForm.getValues("mec_creditos")) || 0;
        if (creditos > 0) {
          lancamentoForm.setValue("valor", Number((creditos * servico.valor).toFixed(2)));
        } else {
          lancamentoForm.setValue("valor", servico.valor);
        }
      } else {
        lancamentoForm.setValue("valor", servico.valor);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="revenda-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Revenda</h2>
          <p className="text-muted-foreground">Controle de valores para revendas</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </Button>
          <Button onClick={() => handleOpenRevendaDialog()} data-testid="new-revenda-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nova Revenda
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Store className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Revendas</p>
                <p className="text-xl font-bold number-display">{stats.total_revendas}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <DollarSign className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Soma Total</p>
                <p className="text-xl font-bold number-display text-blue-500">
                  {formatCurrency(stats.total_soma)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Wallet className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Saldo</p>
                <p className="text-xl font-bold number-display text-emerald-500">
                  {formatCurrency(stats.total_saldo)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <CreditCard className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total a Pagar</p>
                <p className="text-xl font-bold number-display text-amber-500">
                  {formatCurrency(stats.total_a_pagar)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revendas Tabs */}
      {revendas.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Store className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma revenda cadastrada</p>
            <p className="text-sm mt-2">Clique em &quot;Nova Revenda&quot; para adicionar</p>
          </CardContent>
        </Card>
      ) : (() => {
        const filtered = filterNome ? revendas.filter(r => r.nome.toLowerCase().includes(filterNome.toLowerCase())) : revendas;
        return (
          <>
            <div className="flex items-center gap-1 max-w-xs">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Buscar revenda por nome..." value={filterNome} onChange={(e) => setFilterNome(e.target.value)} className="pl-10 h-9" data-testid="search-revenda" />
              </div>
              {filterNome && (
                <button className="shrink-0 p-1 rounded text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => setFilterNome("")}>
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            {filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">Nenhuma revenda encontrada</p>
            ) : (
              <Tabs defaultValue={filtered[0]?.id} key={filtered.map(r=>r.id).join(",")} className="w-full">
                <div className="overflow-x-auto mb-4">
                  <TabsList className="inline-flex w-auto min-w-full h-auto flex-wrap gap-1 p-1" data-testid="revendas-tabs">
                    {filtered.map((r) => (
                      <TabsTrigger key={r.id} value={r.id} className="gap-1.5 whitespace-nowrap" data-testid={`tab-revenda-${r.id}`}>
                        <Store className="w-3.5 h-3.5" /> {r.nome}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                {filtered.map((revenda) => (
                  <TabsContent key={revenda.id} value={revenda.id}>
                    <RevendaCard
                      revenda={revenda}
                      servicos={servicosPorRevenda[revenda.id] || []}
                      onEdit={handleOpenRevendaDialog}
                      onDelete={handleDeleteRevenda}
                      onAddLancamento={handleOpenLancamentoDialog}
                      onEditLancamento={(l, r) => handleOpenLancamentoDialog(r, l)}
                      onDeleteLancamento={handleDeleteLancamento}
                      onDeleteAllLancamentos={handleOpenDeleteAllDialog}
                      onToggleConfirmado={handleToggleConfirmado}
                      onCopyLink={handleCopyLink}
                      onWhatsApp={handleWhatsAppRevenda}
                      onManageServicos={handleOpenServicoDialog}
                      refreshKey={refreshKey}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </>
        );
      })()}

      {/* Revenda Dialog */}
      <Dialog open={revendaDialogOpen} onOpenChange={setRevendaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRevenda ? "Editar Revenda" : "Nova Revenda"}</DialogTitle>
            <DialogDescription>
              {editingRevenda ? "Atualize os dados da revenda" : "Cadastre uma nova revenda"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={revendaForm.handleSubmit(handleSubmitRevenda)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome da Revenda *</Label>
              <Input
                id="nome"
                placeholder="Nome da revenda"
                {...revendaForm.register("nome")}
              />
              {revendaForm.formState.errors.nome && (
                <p className="text-sm text-destructive">{revendaForm.formState.errors.nome.message}</p>
              )}
            </div>

            {/* PIX 1 */}
            <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30 space-y-3">
              <p className="text-sm font-medium text-emerald-500">PIX 1</p>
              <div className="space-y-2">
                <Label htmlFor="pix_1">Chave PIX</Label>
                <Input
                  id="pix_1"
                  placeholder="Ex: email@exemplo.com, CPF, telefone ou chave aleatória"
                  {...revendaForm.register("pix_1")}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="pix_1_banco">Banco</Label>
                  <Input
                    id="pix_1_banco"
                    placeholder="Ex: Nubank, Itaú, etc."
                    {...revendaForm.register("pix_1_banco")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pix_1_nome">Nome Completo</Label>
                  <Input
                    id="pix_1_nome"
                    placeholder="Nome do titular"
                    {...revendaForm.register("pix_1_nome")}
                  />
                </div>
              </div>
            </div>

            {/* PIX 2 */}
            <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30 space-y-3">
              <p className="text-sm font-medium text-emerald-500">PIX 2</p>
              <div className="space-y-2">
                <Label htmlFor="pix_2">Chave PIX</Label>
                <Input
                  id="pix_2"
                  placeholder="Ex: email@exemplo.com, CPF, telefone ou chave aleatória"
                  {...revendaForm.register("pix_2")}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="pix_2_banco">Banco</Label>
                  <Input
                    id="pix_2_banco"
                    placeholder="Ex: Nubank, Itaú, etc."
                    {...revendaForm.register("pix_2_banco")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pix_2_nome">Nome Completo</Label>
                  <Input
                    id="pix_2_nome"
                    placeholder="Nome do titular"
                    {...revendaForm.register("pix_2_nome")}
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="w-4 h-4 text-blue-500" />
                <p className="text-sm font-medium text-blue-500">Link de Visualização</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Após cadastrar, clique no ícone 🔗 para copiar o link de visualização. 
                A revenda poderá ver seus lançamentos sem precisar de login.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRevendaDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Salvando..." : editingRevenda ? "Atualizar" : "Cadastrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lancamento Dialog */}
      <Dialog open={lancamentoDialogOpen} onOpenChange={setLancamentoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingLancamento ? "Editar Lançamento" : "Novo Lançamento"}</DialogTitle>
            <DialogDescription>
              {selectedRevenda?.nome}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={lancamentoForm.handleSubmit(handleSubmitLancamento)} className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={lancamentoForm.watch("tipo_credito") === "MEC" ? "default" : "outline"} className="w-full" onClick={() => lancamentoForm.setValue("tipo_credito", "MEC")} data-testid="btn-tipo-mec">MEC</Button>
                <Button type="button" variant={lancamentoForm.watch("tipo_credito") === "Creditos" ? "default" : "outline"} className="w-full" onClick={() => lancamentoForm.setValue("tipo_credito", "Creditos")} data-testid="btn-tipo-creditos">Creditos</Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mec_creditos">
                {lancamentoForm.watch("tipo_credito") === "MEC" ? "MEC" : "Quantidade (Creditos)"}
              </Label>
              {lancamentoForm.watch("tipo_credito") === "MEC" ? (
                <Input
                  id="mec_creditos"
                  type="text"
                  placeholder="Digite o MEC..."
                  {...lancamentoForm.register("mec_creditos")}
                />
              ) : (
                <Input
                  id="mec_creditos"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="Quantidade de creditos"
                  {...lancamentoForm.register("mec_creditos", {
                    onChange: (e) => {
                      const creditos = parseFloat(e.target.value) || 0;
                      const servicoNome = lancamentoForm.getValues("servico");
                      if (servicoNome && selectedRevenda) {
                        const servicos = servicosPorRevenda[selectedRevenda.id] || [];
                        const servico = servicos.find(s => s.nome === servicoNome);
                        if (servico && creditos > 0) {
                          lancamentoForm.setValue("valor", Number((creditos * servico.valor).toFixed(2)));
                        }
                      }
                    }
                  })}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="data">Data *</Label>
              <Input
                id="data"
                type="date"
                {...lancamentoForm.register("data")}
              />
              {lancamentoForm.formState.errors.data && (
                <p className="text-sm text-destructive">{lancamentoForm.formState.errors.data.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Serviço <span className="text-muted-foreground text-xs">(opcional)</span></Label>
              <Select
                value={lancamentoForm.watch("servico") ? 
                  (servicosPorRevenda[selectedRevenda?.id] || []).find(s => s.nome === lancamentoForm.watch("servico"))?.id || "" 
                  : ""}
                onValueChange={handleSelectServico}
              >
                <SelectTrigger data-testid="select-servico">
                  <SelectValue placeholder="Selecione um serviço (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {(servicosPorRevenda[selectedRevenda?.id] || []).map((servico) => (
                    <SelectItem key={servico.id} value={servico.id}>
                      {servico.nome} - {formatCurrency(servico.valor, servico.moeda)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" {...lancamentoForm.register("servico")} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valor">Valor *</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  {...lancamentoForm.register("valor")}
                />
                {lancamentoForm.formState.errors.valor && (
                  <p className="text-sm text-destructive">{lancamentoForm.formState.errors.valor.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Moeda *</Label>
                <Select
                  value={lancamentoForm.watch("moeda") || "R$"}
                  onValueChange={(v) => lancamentoForm.setValue("moeda", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="R$">R$ (Real)</SelectItem>
                    <SelectItem value="€">€ (Euro)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Valor Total calculado */}
            {(() => {
              const tipo = lancamentoForm.watch("tipo_credito") || "MEC";
              if (tipo === "MEC") return null;
              const creditos = parseFloat(lancamentoForm.watch("mec_creditos")) || 0;
              const valor = parseFloat(lancamentoForm.watch("valor")) || 0;
              const moeda = lancamentoForm.watch("moeda") || "R$";
              const servicoNome = lancamentoForm.watch("servico");
              const servicos = servicosPorRevenda[selectedRevenda?.id] || [];
              const servico = servicos.find(s => s.nome === servicoNome);
              if (creditos > 0 && servico) {
                return (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                    <p className="text-xs text-muted-foreground mb-1">{creditos} Creditos x {formatCurrency(servico.valor, moeda)} = </p>
                    <p className="text-lg font-bold text-emerald-400">Valor Total: {formatCurrency(valor, moeda)}</p>
                  </div>
                );
              }
              return null;
            })()}

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                placeholder="Observações adicionais"
                {...lancamentoForm.register("descricao")}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLancamentoDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Salvando..." : editingLancamento ? "Atualizar" : "Cadastrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Servico Dialog */}
      <Dialog open={servicoDialogOpen} onOpenChange={setServicoDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Gerenciar Serviços</DialogTitle>
            <DialogDescription>
              {selectedRevenda?.nome} - Cadastre os serviços e valores específicos para esta revenda
            </DialogDescription>
          </DialogHeader>
          
          {/* Lista de serviços existentes */}
          {selectedRevenda && (servicosPorRevenda[selectedRevenda.id] || []).length > 0 && (
            <div className="mb-4 p-3 bg-muted/30 rounded-lg max-h-48 overflow-y-auto">
              <p className="text-xs font-medium text-muted-foreground mb-2">Serviços cadastrados:</p>
              <div className="space-y-2">
                {(servicosPorRevenda[selectedRevenda.id] || []).map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2 bg-background rounded border">
                    <div className="flex items-center gap-2">
                      {s.moeda === "€" ? <Euro className="w-4 h-4 text-blue-500" /> : <DollarSign className="w-4 h-4 text-emerald-500" />}
                      <span className="font-medium">{s.nome}</span>
                      <span className="text-muted-foreground">-</span>
                      <span className="font-semibold">{formatCurrency(s.valor, s.moeda)}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setEditingServico(s);
                          servicoForm.reset({ nome: s.nome, valor: s.valor, moeda: s.moeda });
                        }}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => handleDeleteServico(s.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Formulário para adicionar/editar */}
          <form onSubmit={servicoForm.handleSubmit(handleSubmitServico)} className="space-y-4">
            <div className="p-3 border rounded-lg bg-muted/10">
              <p className="text-sm font-medium mb-3">
                {editingServico ? "Editar Serviço" : "Adicionar Novo Serviço"}
              </p>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="servico_nome">Nome do Serviço *</Label>
                  <Input
                    id="servico_nome"
                    placeholder="Ex: UniTV, IPTV Premium, etc."
                    {...servicoForm.register("nome")}
                  />
                  {servicoForm.formState.errors.nome && (
                    <p className="text-sm text-destructive">{servicoForm.formState.errors.nome.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="servico_valor">Valor *</Label>
                    <Input
                      id="servico_valor"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                      {...servicoForm.register("valor")}
                    />
                    {servicoForm.formState.errors.valor && (
                      <p className="text-sm text-destructive">{servicoForm.formState.errors.valor.message}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Moeda *</Label>
                    <Select
                      value={servicoForm.watch("moeda") || "R$"}
                      onValueChange={(v) => servicoForm.setValue("moeda", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="R$">R$ (Real)</SelectItem>
                        <SelectItem value="€">€ (Euro)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              {editingServico && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => {
                    setEditingServico(null);
                    servicoForm.reset({ nome: "", valor: 0, moeda: "R$" });
                  }}
                >
                  Cancelar Edição
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setServicoDialogOpen(false)}>
                Fechar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Salvando..." : editingServico ? "Atualizar Serviço" : "Adicionar Serviço"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir <strong>TODOS</strong> os lançamentos da revenda <strong>{revendaToDeleteAll?.nome}</strong>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/30">
            <p className="text-sm text-destructive font-medium">
              ⚠️ Esta ação não pode ser desfeita!
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Todos os lançamentos serão permanentemente removidos e os valores de Saldo e Valor a Pagar serão zerados.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setDeleteAllDialogOpen(false);
                setRevendaToDeleteAll(null);
              }}
            >
              Não, Cancelar
            </Button>
            <Button 
              type="button" 
              variant="destructive"
              onClick={handleConfirmDeleteAll}
            >
              Sim, Excluir Todos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Funcionarios */}
      <Dialog open={dConfigFunc} onOpenChange={setDConfigFunc}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Gerenciar Funcionarios</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => { e.preventDefault(); if (!novoFunc.trim()) return; try { await authAxios.post("/hub-empresa/funcionarios", { nome: novoFunc.trim() }); toast.success("Funcionario criado!"); setNovoFunc(""); fetchFuncionarios(); } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); } }} className="flex gap-2">
              <Input value={novoFunc} onChange={(e) => setNovoFunc(e.target.value)} placeholder="Nome do funcionario..." className="flex-1" />
              <Button type="submit" size="sm"><Plus className="w-4 h-4" /></Button>
            </form>
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {funcionarios.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum funcionario cadastrado</p>
              ) : funcionarios.map((f) => (
                <div key={f.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{f.nome}</p>
                    <p className="text-xs text-muted-foreground truncate">{`${window.location.origin}/hub-empresa-func/${f.token}`}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={async () => { const url = `${window.location.origin}/hub-empresa-func/${f.token}`; try { await navigator.clipboard.writeText(url); toast.success("Link copiado!"); } catch { toast.info(url); } }}><Copy className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async () => { if (!window.confirm(`Excluir ${f.nome}?`)) return; try { await authAxios.delete(`/hub-empresa/funcionarios/${f.id}`); toast.success("Removido!"); fetchFuncionarios(); } catch { toast.error("Erro"); } }}><X className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
